import { ThemeEvents } from '@theme/events';

/**
 * Keeps the LPE upsell color swatch in sync with the parent product's selected color.
 */
class LpeUpsell extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Ensure sync if Liquid preselection didn't match (e.g. after client-side navigation)
    this.#syncFromParentPicker();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #lpeProductId() {
    return this.querySelector('swatches-variant-picker-component')?.dataset.productId ?? '';
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    // Ignore updates that originated from this upsell's own picker
    if (!this.#lpeProductId || event.detail.data.productId === this.#lpeProductId) return;

    const options = event.detail.resource?.options;
    if (!Array.isArray(options) || options.length === 0) return;

    this.#syncToColor(options);
  };

  #syncFromParentPicker() {
    const section = this.closest('.shopify-section');
    const parentPicker = section?.querySelector(
      'variant-picker[data-template-product-match="true"], variant-picker[ref="mainVariantPicker"]'
    );
    if (!parentPicker) return;

    const selectedValues = Array.from(
      parentPicker.querySelectorAll('fieldset input:checked, select option[selected]')
    )
      .map((el) => {
        if (el instanceof HTMLInputElement || el instanceof HTMLOptionElement) {
          return el.value || el.getAttribute('aria-label') || '';
        }
        return '';
      })
      .filter(Boolean);

    if (selectedValues.length) {
      this.#syncToColor(selectedValues);
    }
  }

  /**
   * Selects the LPE swatch whose color matches one of the given option values.
   * @param {string[]} optionValues
   */
  #syncToColor(optionValues) {
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('swatches-variant-picker-component input[type="radio"]')
    );

    /** @type {HTMLInputElement | null} */
    let match = null;

    for (const input of inputs) {
      const label = input.getAttribute('aria-label') || '';
      const value = input.value || '';

      if (optionValues.some((opt) => opt === value || opt === label)) {
        match = input;
        break;
      }
    }

    if (!match) return;

    if (!match.checked) {
      match.checked = true;
    }

    this.#applyVariant(match);
  }

  /**
   * Updates the add-to-cart form variant id to match the selected swatch.
   * @param {HTMLInputElement} input
   */
  #applyVariant(input) {
    const variantId = input.dataset.variantId || input.dataset.firstAvailableOrFirstVariantId;
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('product-form-component input[name="id"]')
    );

    if (variantId && variantInput) {
      variantInput.value = variantId;
    }
  }
}

if (!customElements.get('lpe-upsell')) {
  customElements.define('lpe-upsell', LpeUpsell);
}
